import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Box, Inline } from '@atlaskit/primitives/compiled';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Grid } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import { cssMap } from '@atlaskit/css';

import { ErrorDisplay, isUnauthorizedError } from '@confluence/error-boundary';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';
import { CreateSpaceButton } from '@confluence/create-space/entry-points/createSpace';
import { LAND_IN_SAMPLE_SPACE_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import {
	useSetOnboardingState,
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';

interface AnnouncementBannerProps {
	spaceKey?: string;
}

function isErrorHandled(error: Parameters<typeof markErrorAsHandled>[0]) {
	// Mark unauthorized error as handled and not report to monitoring client
	if (isUnauthorizedError(error) || error?.graphQLErrors?.[0]?.message === 'unauthorized') {
		markErrorAsHandled(error);
		return true;
	}
	return false;
}

export const i18n = defineMessages({
	announcementText: {
		id: 'experiment-land-in-sample-space-xflow.announcement.text',
		defaultMessage:
			'When you’re ready, try adding content here or create a fresh space from scratch. {createSpaceLink}',
		description: 'Text for the announcement banner',
	},
	bannerDismiss: {
		id: 'experiment-land-in-sample-space-xflow.announcement.dismiss',
		defaultMessage: 'Dismiss',
		description: 'clicking this icon closes the feedback banner',
	},
});

const styles = cssMap({
	banner: {
		padding: token('space.050'),
		borderBottomColor: token('color.border'),
		borderBottomStyle: 'solid',
		borderBottomWidth: token('border.width'),
		backgroundColor: token('color.background.accent.purple.subtlest'),
		color: token('color.text.subtle'),
		zIndex: 400,
	},
	dismiss: {
		display: 'flex',
		justifySelf: 'end',
		gridArea: 'close',
	},
	message: {
		gridArea: 'message',
	},
});

export const AnnouncementBanner = ({ spaceKey }: AnnouncementBannerProps) => {
	const { formatMessage } = useIntl();
	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();
	const { setOnboardingState } = useSetOnboardingState();
	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState([...Object.values(LAND_IN_SAMPLE_SPACE_ONBOARDING_STATE_KEYS)]);

	const { hasDismissedBanner } = deserializeState(onboardingStateData);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	if (spaceKey !== 'XFLOW') {
		return null;
	}

	const handleDismissClick = () => {
		setOnboardingState({
			key: LAND_IN_SAMPLE_SPACE_ONBOARDING_STATE_KEYS.hasDismissedBanner,
			value: 'true',
		});
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'sampleSpaceAccouncementBannerDismissButton',
				source: 'jiraConnectedSampleSpaceAnnouncementBanner',
			},
		}).fire();
	};

	if (onboardingStateError && !isErrorHandled(onboardingStateError)) {
		return (
			<>
				<ErrorDisplay error={onboardingStateError} />
			</>
		);
	}

	if (hasDismissedBanner || onboardingStateLoading) {
		return null;
	}

	return (
		<div data-vc="x-flow-sample-space-announcement-banner" {...ssrPlaceholderIdProp}>
			<Grid
				templateAreas={['empty message close']}
				templateColumns="1fr auto 1fr"
				xcss={styles.banner}
			>
				<Inline space="space.100" alignInline="center" alignBlock="center" xcss={styles.message}>
					<FormattedMessage
						{...i18n.announcementText}
						values={{
							createSpaceLink: (
								<CreateSpaceButton
									appearance="link-button"
									icon={<ArrowRightIcon label="" />}
									source="jiraConnectedSampleSpaceAnnouncementBanner"
								/>
							),
						}}
					/>
				</Inline>
				<Box xcss={styles.dismiss}>
					<IconButton
						appearance="subtle"
						spacing="compact"
						icon={CrossIcon}
						label={formatMessage(i18n.bannerDismiss)}
						isTooltipDisabled={false}
						onClick={handleDismissClick}
					/>
				</Box>
			</Grid>
		</div>
	);
};
